import React, { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { TextLink } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import { trackMembershipLearnMore } from '../../actions/membership';
import { mobile } from '../../styles';
import { Paragraph } from '../../styles/paragraph';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { hasMembershipInCart as hasMembershipInCartSelector } from '../../utils/selectors';
import { useMembership } from '../../utils/useMembership';
import { Component as Price } from '../ExtendedPrice';
import { useProductDetailContext } from '../ProductDetailLayout/ProductDetailContext';
import VIPLearnMoreModal from '../VIPLearnMoreModal/VIPLearnMoreModal';

export const LearnMoreLinkSingleCTA = styled(TextLink)`
  display: block;
  flex: 0 0 auto;
  position: relative;
  padding: 0px 0px 3px;
  background: transparent;
  cursor: pointer;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.lavender600};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lavender600};
  margin-top: 30px;
  margin-bottom: 30px;
  font-size: 14px;
  line-height: 18px;
  text-align: center;
`;

export const LearnMoreLinkInline = styled(TextLink)`
  flex: 0 0 auto;
  position: relative;
  padding: 0px 0px 3px;
  background: transparent;
  cursor: pointer;

  ${({ theme }) => theme.links.variants.textLinkTertiary}
  color: ${({ theme }) => theme.colors.black};
  &:hover {
    border-bottom: 1px solid ${({ theme }) => theme.colors.black};
  }
`;

export const OptionToolTip = styled.div`
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.18px;
  color: ${props => props.theme.colors.default};
  text-align: left;
  margin-top: 14px;
  margin-bottom: 14px;
  ${mobile`
    padding: 0 ${props => props.theme.spacing.small}px;
  `}
  ${({ memberOptionSelected }) =>
    memberOptionSelected &&
    `
    font-size:12px;
    line-height:18px;
  `}
  ${({ extraTopSpacing }) =>
    extraTopSpacing &&
    `
    margin-top:24px;
  `}
`;

const Details = styled.div`
  display: inline-block;
`;

const ItemData = styled(Paragraph).attrs({ size: 12 })`
  font-weight: normal;
`;

export const ProductDetailPriceModeLegalText = ({
  location,
  shouldNotShowCtaForSports,
}) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const { price: membershipPrice } = useMembership();
  const { 'emp-membership-onboarding': isEmpFeatureEnabled } = useLDFlags();
  const {
    singleCTA: { enabled: singleCTAFlagEnabled, priceOptionSelected },
  } = useProductDetailContext();
  const hasMembershipInCart = useSelector(hasMembershipInCartSelector);

  const handleOpenModal = event => {
    event.preventDefault();
    if (!shouldNotShowCtaForSports) {
      dispatch(trackMembershipLearnMore(location));
      setShowModal(true);
    }
  };

  const shouldDisplayMembershipSpecialText =
    singleCTAFlagEnabled &&
    (priceOptionSelected === 'member' || hasMembershipInCart);

  const renderLearnMoreLink = () => {
    if (shouldDisplayMembershipSpecialText) {
      return (
        <LearnMoreLinkSingleCTA
          onClick={handleOpenModal}
          data-autotag="pdpLearnMoreLnk"
        >
          <FormattedMessage
            id="site_pdp.single_cta_member_learn_more_link"
            defaultMessage="LEARN MORE ABOUT MEMBERSHIP REWARDS"
          />
        </LearnMoreLinkSingleCTA>
      );
    }

    return (
      <Details>
        <ItemData>
          <LearnMoreLinkInline
            onClick={handleOpenModal}
            data-autotag="pdpLearnMoreLnk"
          >
            {isEmpFeatureEnabled ? (
              <FormattedMessage
                defaultMessage="Learn more"
                id="global_checkout.emp_membership_learn_more_link"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Learn more"
                id="global_checkout.membership_learn_more_link"
              />
            )}
          </LearnMoreLinkInline>
        </ItemData>
      </Details>
    );
  };

  // SXWS-758 requires that the legal text is not displayed if member is not selected
  // or if membership item is not in cart
  if (
    singleCTAFlagEnabled &&
    priceOptionSelected !== 'member' &&
    !hasMembershipInCart
  ) {
    return <></>;
  }

  const price = <Price amount={membershipPrice} />;

  return (
    <>
      <OptionToolTip
        memberOptionSelected={shouldDisplayMembershipSpecialText}
        extraTopSpacing={singleCTAFlagEnabled && hasMembershipInCart}
        data-autotag="pdpVIPctaLegalTextLoc"
      >
        {isEmpFeatureEnabled ? (
          <FormattedMessage
            id="site_pdp.shop_emp_tip"
            defaultMessage="After your first purchase, you will be charged {price} each month if you don’t shop or skip by the 5th. Cancel anytime. {learnMoreLink}"
            values={{
              price: price,
              learnMoreLink: renderLearnMoreLink(),
            }}
          />
        ) : (
          <FormattedMessage
            id="site_pdp.shop_vip_tip"
            defaultMessage="After your first purchase, you'll be billed {price} each month to unlock a Member Credit and other rewards, unless you 'Skip The Month' between the 1st and 5th. Member Credits never expire. Cancel anytime. {learnMoreLink}"
            values={{
              price: price,
              learnMoreLink: renderLearnMoreLink(),
            }}
          />
        )}
      </OptionToolTip>

      <VIPLearnMoreModal
        blur
        isOpen={showModal}
        onExit={() => setShowModal(false)}
      />
    </>
  );
};
